<template>
  <div>
    <VCard class="pa-0 pt-4">
      <VRow class="ma-0">
        <VCol cols="12" :class="smAndDown ? 'pa-0' : ''">
          <TemplateSearch class="ma-0">
            <template #searchTitle>
              <div class="d-flex align-center">
                <VIcon icon="tabler-search" class="mr-2" />
                搜尋條件
              </div>
            </template>
            <template #search>
              <VRow class="ma-0 search-filters-row" dense>
                <VCol cols="12" sm="6" md="3">
                  <VSelect
                    class="search-filter-control"
                    v-model="filters.system"
                    :items="systemOptions"
                    item-title="title"
                    item-value="value"
                    placeholder="系統"
                    prepend-inner-icon="tabler-apps"
                    density="compact"
                    variant="outlined"
                    hide-details
                    clearable
                  />
                </VCol>
                <VCol cols="12" sm="6" md="3">
                  <VSelect
                    class="search-filter-control"
                    v-model="filters.formKey"
                    :items="formKeyFilterOptions"
                    item-title="title"
                    item-value="value"
                    placeholder="簽核作業"
                    prepend-inner-icon="tabler-file-text"
                    density="compact"
                    variant="outlined"
                    hide-details
                    clearable
                  />
                </VCol>
                <VCol cols="12" sm="6" md="3">
                  <AppInput
                    v-model="filters.proxy"
                    type="picker"
                    item-title="label"
                    item-value="value"
                    placeholder="處理人"
                    :picker="duUserPicker"
                    :queryAll="false"
                    density="compact"
                    clearable
                  />
                </VCol>
                <VCol cols="12" sm="6" md="3">
                  <AppInput
                    v-model="filters.assignee"
                    type="picker"
                    item-title="label"
                    item-value="value"
                    placeholder="被代理人"
                    :picker="duUserPicker"
                    :queryAll="false"
                    density="compact"
                    clearable
                  />
                </VCol>
                <VCol cols="12" sm="6" md="3">
                  <VTextField
                    class="search-filter-control"
                    v-model="filters.applicationNo"
                    placeholder="單號"
                    prepend-inner-icon="tabler-hash"
                    density="compact"
                    variant="outlined"
                    hide-details
                    clearable
                  />
                </VCol>
                <VCol cols="12" sm="6" md="3">
                  <AppDateTimePicker
                    v-model="filters.dateFrom"
                    placeholder="處理日起"
                    prepend-inner-icon="tabler-calendar-event"
                    density="compact"
                    variant="outlined"
                    hide-details
                    clearable
                  />
                </VCol>
                <VCol cols="12" sm="6" md="3">
                  <AppDateTimePicker
                    v-model="filters.dateTo"
                    placeholder="處理日迄"
                    prepend-inner-icon="tabler-calendar-event"
                    density="compact"
                    variant="outlined"
                    hide-details
                    clearable
                  />
                </VCol>
              </VRow>
            </template>
          </TemplateSearch>
        </VCol>
      </VRow>

      <div class="px-4 py-2 d-flex align-center justify-space-between flex-wrap ga-2 bg-surface">
        <div class="d-flex align-center ga-2">
          <VIcon icon="tabler-list-check" size="22" class="text-primary" />
          <span class="text-h6 font-weight-bold">代理清單</span>
        </div>
        <VBtn
          size="small"
          class="rounded-pill"
          variant="tonal"
          color="primary"
          prepend-icon="tabler-refresh"
          :loading="loading"
          @click="fetchData"
        >
          重新整理
        </VBtn>
      </div>

      <div class="px-4 pb-4">
        <VDataTableServer
          v-model:items-per-page="size"
          v-model:page="page"
          :items-length="total"
          :headers="headers"
          :items="items"
          :loading="loading"
          class="rich-data-table"
          hover
          hide-default-footer
        >
          <template #item.system="{ item }">
            <VChip
              size="small"
              variant="flat"
              class="rounded-pill"
              :color="formKeySystemChipColor(item._system)"
            >
              {{ formKeySystemTitle(item._system) }}
            </VChip>
          </template>
          <template #item.formKey="{ item }">
            <span :title="formKeyDisplay(item)">
              {{ formKeyDisplay(item) }}
            </span>
          </template>
          <template #item.applicationNo="{ item }">
            <v-btn
              v-if="item.applicationNo && item.url"
              variant="text"
              color="primary"
              class="font-weight-bold px-2"
              @click.stop="openForm(item)"
            >
              <VIcon icon="tabler-hash" size="14" class="me-1 text-medium-emphasis" />
              {{ item.applicationNo }}
            </v-btn>
            <span v-else-if="item.applicationNo">{{ item.applicationNo }}</span>
            <span v-else class="text-disabled">—</span>
          </template>
          <template #item.proxy="{ item }">
            <span class="text-no-wrap">{{ userLabel(item.proxy, item.proxyName) }}</span>
          </template>
          <template #item.assignee="{ item }">
            <span class="text-no-wrap">{{ userLabel(item.assignee, item.assigneeName) }}</span>
          </template>
          <template #item.processTime="{ item }">
            <span class="text-no-wrap">{{ rocCompact(item.processTime) || item.processTimeDisplay }}</span>
          </template>
          <template #no-data>
            <div class="text-center py-12">
              <VIcon icon="tabler-list-check" size="56" class="text-disabled mb-3" />
              <div class="text-h6 text-medium-emphasis mb-1">查無代理處理紀錄</div>
              <div class="text-body-2 text-disabled">代理人代為簽核後，會顯示於本清單</div>
            </div>
          </template>
        </VDataTableServer>

        <div v-if="items.length || total > 0" class="mt-3">
          <TablePagination
            v-model:page="page"
            v-model:size="size"
            :items-per-page="size"
            :total-items="total"
          />
        </div>
      </div>
    </VCard>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useDisplay } from "vuetify";
import TemplateSearchComp from "@/components/template/TemplateSearch.vue";
import { assigneeProxy } from "../api/assigneeProxyApiUrl";

const TemplateSearch = TemplateSearchComp as any;
const { smAndDown } = useDisplay();

definePage({
  meta: {
    action: "manage",
    subject: "ASSIGNEEPROXYSETTING",
  },
});

const FORM_KEY_TAB_ID = "FormKey";
const SEARCH_SYSTEM_PREFIX = "sys:";

type FormKeyOption = { value: string; title: string; system: string };
const FORM_KEY_SYSTEM_META: Record<string, { title: string; order: number }> = {
  "1": { title: "協會系統", order: 1 },
  "2": { title: "選手系統", order: 2 },
  "3": { title: "消耗品系統", order: 3 },
  "0": { title: "其他", order: 9 },
};
const normalizeFormKeySystem = (raw: unknown): string => {
  if (raw == null || raw === "") return "0";
  const n = Number(raw);
  if (!Number.isNaN(n)) {
    const s = String(Math.trunc(n));
    if (s in FORM_KEY_SYSTEM_META) return s;
  }
  const s = String(raw).trim();
  return s in FORM_KEY_SYSTEM_META ? s : "0";
};
const formKeySystemTitle = (system: string) =>
  FORM_KEY_SYSTEM_META[system]?.title ?? "其他";
const formKeySystemChipColor = (system: unknown) => {
  const sys = normalizeFormKeySystem(system);
  if (sys === "1") return "info";
  if (sys === "2") return "warning";
  if (sys === "3") return "success";
  return "secondary";
};

const formKeyOptions = ref<FormKeyOption[]>([]);
const formKeyMap = reactive<Record<string, string>>({});
const formKeySystemMap = reactive<Record<string, string>>({});
const formKeyTitle = (code: string) => {
  if (!code) return "";
  return formKeyMap[code] || code;
};

const isOrganizerCorrection = (item: any) => {
  const activityId = String(item?.activityId || "").trim();
  const subject = String(item?.formSubject || "");
  return activityId === "FReturn" || subject.includes("[承辦修正]");
};

const formKeyDisplay = (item: any) => {
  const title = formKeyTitle(item?.formKey) || item?.formKey || "—";
  if (!isOrganizerCorrection(item)) return title;
  if (String(title).includes("[承辦修正]")) return title;
  return `${title} [承辦修正]`;
};

const systemOptions = computed(() =>
  (["1", "2", "3"] as const).map((value) => ({
    title: formKeySystemTitle(value),
    value: `${SEARCH_SYSTEM_PREFIX}${value}`,
  })),
);

const formKeyFilterOptions = computed(() => {
  const sys = filters.system?.startsWith(SEARCH_SYSTEM_PREFIX)
    ? filters.system.slice(SEARCH_SYSTEM_PREFIX.length)
    : "";
  return formKeyOptions.value.filter((o) => !sys || o.system === sys);
});

const filters = reactive({
  system: null as string | null,
  formKey: null as string | null,
  proxy: "",
  assignee: "",
  applicationNo: "",
  dateFrom: "",
  dateTo: "",
});

watch(
  () => filters.system,
  () => {
    filters.formKey = null;
  },
);

const page = ref(1);
const size = ref(10);
const total = ref(0);
const loading = ref(false);
const items = ref<any[]>([]);

const headers = [
  { title: "系統", key: "system", sortable: false, width: "120px" },
  { title: "簽核作業", key: "formKey", sortable: false },
  { title: "單號", key: "applicationNo", sortable: false, width: "200px" },
  { title: "處理人", key: "proxy", sortable: false, width: "160px" },
  { title: "被代理人", key: "assignee", sortable: false, width: "160px" },
  { title: "處理時間", key: "processTime", sortable: false, width: "180px" },
];

const ymd8 = (d: string) => (d ? String(d).replace(/-/g, "") : "");
const userLabel = (code: string, name?: string) => {
  const c = toCode(code);
  if (!c) return "";
  const n = (name || "").trim();
  return n ? `${n}(${c})` : c;
};
const rocCompact = (s: any) => {
  if (!s) return "";
  const v = String(s).trim();
  if (v.length < 8) return v;
  const y = parseInt(v.slice(0, 4), 10) - 1911;
  const hh = v.length >= 10 ? v.slice(8, 10) : "00";
  const mi = v.length >= 12 ? v.slice(10, 12) : "00";
  const ss = v.length >= 14 ? v.slice(12, 14) : "00";
  return `${y}-${v.slice(4, 6)}-${v.slice(6, 8)} ${hh}:${mi}:${ss}`;
};

const resolveFormKeyQuery = () => {
  if (filters.formKey) return filters.formKey;
  if (!filters.system) return "";
  const sys = filters.system.startsWith(SEARCH_SYSTEM_PREFIX)
    ? filters.system.slice(SEARCH_SYSTEM_PREFIX.length)
    : "";
  if (!sys) return "";
  return formKeyOptions.value
    .filter((o) => o.system === sys)
    .map((o) => o.value)
    .join(",");
};

const enrichItem = (row: any) => {
  const fk = String(row?.formKey || "");
  return {
    ...row,
    _system: normalizeFormKeySystem(formKeySystemMap[fk] ?? "0"),
  };
};

const fetchFormKeyOptions = async () => {
  try {
    const resp: any = await $api("/q/de/select/select/select/deSelectField23", {
      method: "GET",
      query: { tabId: FORM_KEY_TAB_ID },
    });
    const list: any[] = Array.isArray(resp) ? resp : (resp?.data ?? []);
    const seen = new Set<string>();
    const opts: FormKeyOption[] = [];
    for (const it of list) {
      if (!it?.value) continue;
      const value = String(it.value).trim();
      if (!value || seen.has(value)) continue;
      seen.add(value);
      const title = String(it.label ?? value).trim() || value;
      const system = normalizeFormKeySystem(it.field4 ?? it.desc);
      opts.push({ value, title, system });
      formKeyMap[value] = title;
      formKeySystemMap[value] = system;
    }
    formKeyOptions.value = opts;
  } catch (error) {
    console.error("取得表單代碼選項失敗:", error);
  }
};

const fetchData = async () => {
  loading.value = true;
  try {
    const resp: any = await $api(assigneeProxy.processPage, {
      method: "GET",
      query: {
        formKey: resolveFormKeyQuery(),
        assignee: filters.assignee ? toCode(filters.assignee) : "",
        proxy: filters.proxy ? toCode(filters.proxy) : "",
        applicationNo: filters.applicationNo?.trim() || "",
        dateFrom: filters.dateFrom ? ymd8(filters.dateFrom) : "",
        dateTo: filters.dateTo ? ymd8(filters.dateTo) : "",
        pageNo: Math.max(page.value - 1, 0),
        pageSize: size.value,
      },
    });
    total.value = resp?.totalElements ?? 0;
    const content = resp?.content ?? [];
    items.value = content.map(enrichItem);
  } catch (error) {
    total.value = 0;
    items.value = [];
    console.error("查詢代理清單失敗:", error);
  } finally {
    loading.value = false;
  }
};

const openForm = (item: any) => {
  const raw = String(item?.url || "").trim();
  if (!raw) return;
  let path = raw;
  try {
    if (/^https?:\/\//i.test(raw)) {
      const u = new URL(raw);
      path = u.pathname + u.search;
    }
  } catch {
    path = raw;
  }
  if (!path.startsWith("/")) path = `/${path}`;
  const crossApp = path.startsWith("/ath/") || path.startsWith("/stk/") || path.startsWith("/tca/");
  if (crossApp) {
    window.open(`${window.location.origin}${path}`, "_blank");
    return;
  }
  window.open(path, "_blank");
};

let fetchTimer: ReturnType<typeof setTimeout> | null = null;
const scheduleFetch = () => {
  if (fetchTimer) clearTimeout(fetchTimer);
  fetchTimer = setTimeout(() => {
    if (page.value !== 1) {
      page.value = 1;
    } else {
      fetchData();
    }
  }, 250);
};

watch(filters, scheduleFetch, { deep: true });
watch([page, size], () => fetchData());

onMounted(async () => {
  await fetchFormKeyOptions();
  fetchData();
});
</script>

<style scoped>
.search-filters-row {
  --search-field-height: 44px;
  --search-field-font-size: 1rem;
}
.search-filters-row :deep(.v-input),
.search-filters-row :deep(.v-input__control),
.search-filters-row :deep(.v-field) {
  --v-input-control-height: var(--search-field-height);
}
.search-filters-row :deep(.v-field) {
  min-height: var(--search-field-height);
  font-size: var(--search-field-font-size);
}

.rich-data-table :deep(thead th) {
  white-space: nowrap;
  font-weight: 700;
  background: rgba(var(--v-theme-on-surface), 0.03);
}
.rich-data-table :deep(tbody tr:nth-child(even) > td) {
  background: rgba(var(--v-theme-on-surface), 0.02);
}
.rich-data-table :deep(tbody td) {
  vertical-align: middle;
}
</style>
